import { toPng } from 'html-to-image';
import { supabase } from '@os/integrations/supabase/client';
import { ensureDir } from '@os/utils/filesystem';
import { globalVFS } from '@os/utils/storage/VFS';
import { format } from 'date-fns';

// Resolve current theme (light/dark) by inspecting MapleOS root
const getCurrentTheme = (): 'light' | 'dark' => {
  const root = document.querySelector('.mapleos-root');
  return root?.classList.contains('dark') ? 'dark' : 'light';
};

// ADD: helper to resolve a proper background fill for html-to-image
const getBackgroundColor = (el: HTMLElement): string => {
  const bg = window.getComputedStyle(el).backgroundColor;
  if (bg && bg !== 'rgba(0, 0, 0, 0)') return bg;
  // Fallbacks per theme
  return getCurrentTheme() === 'dark' ? '#0b0b0b' : '#ffffff';
};

// Convert data URL -> Blob
const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const res = await fetch(dataUrl);
  return await res.blob();
};

// Resolve username (fallback to email handle or default)
const resolveUsername = async (): Promise<string> => {
  const { data: sessionData } = await supabase.auth.getSession();
  const user = sessionData.session?.user;
  let username = 'vincemcmullin';
  if (user?.id) {
    const { data: profile } = await supabase
      .from('profiles')
      .select('username,email')
      .eq('id', user.id)
      .maybeSingle();
    const candidate =
      profile?.username ||
      (profile?.email || user.email || 'User').split('@')[0] ||
      'User';
    username = (candidate.replace(/[^a-zA-Z0-9]/g, '') || 'User');
  }
  return username;
};

// Screenshots folder path: C:\User\<username>\Pictures\Screenshots
export const getUserScreenshotsPath = async (): Promise<string> => {
  const username = await resolveUsername();
  return `C:\\User\\${username}\\Pictures\\Screenshots`;
};

// Ensure screenshots folder exists
const ensureScreenshotsFolder = async () => {
  const username = await resolveUsername();
  await ensureDir(`C:\\User\\${username}\\Pictures`);
  const base = await getUserScreenshotsPath();
  await ensureDir(base);
  return base;
};

// Capture an element to PNG data URL
export const captureElementToPng = async (el: HTMLElement): Promise<string> => {
  const dpr = Math.max(1, window.devicePixelRatio || 1);
  return await toPng(el, {
    pixelRatio: dpr,
    cacheBust: true,
  });
};

// Capture entire MapleOS desktop while excluding elements marked data-ignore-capture
export const captureDesktopToPng = async (): Promise<string> => {
  const root =
    (document.querySelector('.mapleos-root') as HTMLElement) ||
    (document.body as HTMLElement);
  const dpr = Math.max(1, window.devicePixelRatio || 1);

  return await toPng(root, {
    pixelRatio: dpr,
    cacheBust: true,
    backgroundColor: getBackgroundColor(root), // ensure edges anti-alias to real background, not white
    filter: (node) => {
      const el = node as HTMLElement;
      // Exclude any node explicitly marked to ignore capture (we'll mark the ScreenshotTool root)
      return !(el?.getAttribute && el.getAttribute('data-ignore-capture') === 'true');
    },
  });
};

// Upload a screenshot to Supabase private bucket and return a signed URL
const uploadToSupabase = async (blob: Blob, name: string, theme: 'light' | 'dark') => {
  const { data: sessionData } = await supabase.auth.getSession();
  const userId = sessionData.session?.user?.id || 'anonymous';
  const bucket = supabase.storage.from('screenshots');

  const key = `users/${userId}/${theme}/${name}`;
  const { error: uploadError } = await bucket.upload(key, blob, {
    contentType: 'image/png',
    upsert: true,
  });
  if (uploadError) {
    throw uploadError;
  }

  // 7 days expiry by default
  const { data: signed, error: signedErr } = await bucket.createSignedUrl(key, 7 * 24 * 60 * 60);
  if (signedErr) {
    throw signedErr;
  }

  return { path: key, signedUrl: signed?.signedUrl || null };
};

export type SaveUploadResult = {
  vfsPath: string;
  supabasePath: string | null;
  signedUrl: string | null;
  theme: 'light' | 'dark';
};

// New: Save-only step (no upload) with timestamped filename
export const saveScreenshotToVFS = async (dataUrl: string, baseName: string): Promise<{ vfsPath: string; fileName: string; theme: 'light' | 'dark'; }> => {
  const theme = getCurrentTheme();
  const folder = await ensureScreenshotsFolder();
  const stamp = format(new Date(), 'yyyy-MM-dd_HHmmss');
  const fileName = `${baseName}_${stamp}.png`;
  const vfsPath = `${folder}\\${fileName}`;
  await globalVFS.save(vfsPath, dataUrl);
  return { vfsPath, fileName, theme };
};

// New: Upload-only step for a given data URL and filename
export const uploadScreenshotFromDataUrl = async (dataUrl: string, fileName: string, theme: 'light' | 'dark'): Promise<{ supabasePath: string; signedUrl: string | null; }> => {
  const blob = await dataUrlToBlob(dataUrl);
  const { path, signedUrl } = await uploadToSupabase(blob, fileName, theme);
  return { supabasePath: path, signedUrl };
};

// Backward-compatible helper: save then upload
export const saveAndUploadScreenshot = async (dataUrl: string, baseName: string): Promise<SaveUploadResult> => {
  const { vfsPath, fileName, theme } = await saveScreenshotToVFS(dataUrl, baseName);
  const { supabasePath, signedUrl } = await uploadScreenshotFromDataUrl(dataUrl, fileName, theme);
  return { vfsPath, supabasePath, signedUrl, theme };
};

// NEW: Crop a desktop screenshot data URL to a specific DOMRect (respecting device pixel ratio)
export const cropDataUrlToRect = async (dataUrl: string, rect: DOMRect, pixelRatio: number): Promise<string> => {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = (e) => reject(e);
    image.src = dataUrl;
  });

  const sx = Math.max(0, Math.floor(rect.left * pixelRatio));
  const sy = Math.max(0, Math.floor(rect.top * pixelRatio));
  const sw = Math.max(1, Math.floor(rect.width * pixelRatio));
  const sh = Math.max(1, Math.floor(rect.height * pixelRatio));

  const canvas = document.createElement('canvas');
  canvas.width = sw;
  canvas.height = sh;
  const ctx = canvas.getContext('2d')!;
  // Improve edge quality (rounded corners) during crop resampling
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  return canvas.toDataURL('image/png');
};